import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface BrregLookupResult {
  name: string;
  address: string;
  address2: string;
  zipCode: string;
  city: string;
  country: string;
  // Set when the number is a sub-unit (underenhet), e.g. a school under its county.
  parentOrgId: string | null;
}

export interface BrregUnit extends BrregLookupResult {
  orgId: string;
}

// Brønnøysundregistrene's public "Enhetsregisteret" API — no auth required.
// https://data.brreg.no/enhetsregisteret/api/docs/index.html
const BASE_URL = 'https://data.brreg.no/enhetsregisteret/api/enheter';
const UNITS_URL = 'https://data.brreg.no/enhetsregisteret/api/underenheter';

@Injectable({ providedIn: 'root' })
export class BrregService {

  constructor(private http: HttpClient) { }

  async lookup(orgNumber: string): Promise<BrregLookupResult> {
    const cleaned = (orgNumber || '').replace(/\s+/g, '');
    if (!/^\d{9}$/.test(cleaned)) {
      throw new Error('Org. number must be 9 digits');
    }

    let enhet: any;
    try {
      enhet = await firstValueFrom(this.http.get(`${BASE_URL}/${cleaned}`));
    } catch (err: any) {
      if (err?.status !== 404) throw new Error('Could not reach the Brønnøysund register');
      // Not a main unit — schools, branches and departments are sub-units.
      try {
        enhet = await firstValueFrom(this.http.get(`${UNITS_URL}/${cleaned}`));
      } catch (err2: any) {
        if (err2?.status === 404) throw new Error('No company found for that org. number');
        throw new Error('Could not reach the Brønnøysund register');
      }
    }
    return this.toResult(enhet);
  }

  // Sub-units registered under a main unit (e.g. every school of a county).
  async listUnits(parentOrgId: string): Promise<BrregUnit[]> {
    const cleaned = (parentOrgId || '').replace(/\s+/g, '');
    if (!/^\d{9}$/.test(cleaned)) return [];
    const res: any = await firstValueFrom(this.http.get(`${UNITS_URL}?overordnetEnhet=${cleaned}&size=500`));
    const list: any[] = res?._embedded?.underenheter || [];
    return list.map(u => ({ orgId: u.organisasjonsnummer, ...this.toResult(u) }));
  }

  private toResult(enhet: any): BrregLookupResult {
    const addr = enhet.forretningsadresse || enhet.beliggenhetsadresse || enhet.postadresse || {};
    const lines: string[] = addr.adresse || [];
    return {
      name: enhet.navn || '',
      address: lines[0] || '',
      address2: lines.slice(1).filter(Boolean).join(', '),
      zipCode: addr.postnummer || '',
      city: addr.poststed || '',
      country: addr.land || 'Norge',
      parentOrgId: enhet.overordnetEnhet || null,
    };
  }
}
